<template>
	<MainLayout>
		<div class="container">
			<div class="box">
				<div class="row">
					<div class="col-12 d-flex justify-content-between align-items-center">
						<h4 class="mb-0">
							<md-icon icon="fact_check" /> Revisión de Evidencias
						</h4>
					</div>
					<div class="col-12"><hr></div>
				</div>

				<div class="row">
					<!-- Filtros  -->
					<div class="col-md-12">
						<div class="row mb-2">
							<div class="col-2">
								<b-form-group label="Res ID" label-size="sm">
									<b-form-input
										size="sm"
										:class="{ 'active-filter': filters.id }"
										v-model="filters.id"
										type="number"
									/>
								</b-form-group>
							</div>
							<div class="col-3">
								<b-form-group label="Fecha de solicitud desde" label-size="sm">
									<b-form-datepicker
										:class="{
											'active-filter': filters.startDate
										}"
										reset-button
										today-button
										size="sm"
										v-model="filters.startDate"
									/>
								</b-form-group>
							</div>
							<div class="col-3">
								<b-form-group label="Fecha de solicitud hasta" label-size="sm">
									<b-form-datepicker
										:class="{
											'active-filter': filters.endDate
										}"
										reset-button
										today-button
										size="sm"
										v-model="filters.endDate"
									/>
								</b-form-group>
							</div>
							<div class="col-3">
								<b-form-group  label="Estado" label-size="sm">
									<b-form-select
										v-model="filters.status"
										:options="statusOptions"
										size="sm"
									></b-form-select>
								</b-form-group>
							</div>

							<div class="col-6">
								<b-form-group label="Proveedor" label-size="sm">
									<multiselect
										id="suppliers"
										:options="supplierOptions"
										v-model="filters.suppliers"
										:multiple="true"
										:show-labels="false"
										placeholder="Todos"
										label="text"
										track-by="value"
										taggable>
										<template v-slot:clear >
											<div class="multiselect__clear" @click="filters.suppliers = []">
											</div>
										</template>
									</multiselect>
								</b-form-group>
							</div>

							<div class="col-3 d-flex align-items-center pt-4">
								<b-form-checkbox
									v-model="filters.expiredLinks"
									switch
								>
									Links caducados
								</b-form-checkbox>
							</div>

							<div class="col-3 my-auto">
								<b-button
									class="w-25"
									variant="primary"
									block
									@click="loadEvidences"
								>
									<md-icon icon="search" />
								</b-button>
							</div>
						</div>
					</div>

					<div class="col-12 text-center py-5" v-if="loading">
						<b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
						<p class="mt-3 text-muted">Cargando evidencias...</p>
					</div>

					<div class="col-12 text-center text-muted py-5" v-else-if="rows.length === 0">
						<md-icon icon="check_circle" style="font-size: 64px;" class="text-success" />
						<h5 class="mt-3">No hay evidencias pendientes</h5>
						<p>Todas las evidencias han sido revisadas</p>
					</div>
	
					<!-- Lista de servicios con acciones integradas -->
					<div class="col-md-7" v-if="rows.length > 0 && !loading">

						<div class="mb-3 d-flex justify-content-between align-items-center">
							<div class="d-flex align-items-center">
								<small class="text-muted mr-2">Mostrar:</small>
								<b-form-select
									v-model="perPage"
									:options="perPageOptions"
									size="sm"
									style="width: 80px;"
									@change="onPerPageChange"
								></b-form-select>
								<small class="text-muted ml-3">
									{{ (currentPage - 1) * perPage + 1 }} - {{ Math.min(currentPage * perPage, totalRows) }} de {{ totalRows }}
								</small>
							</div>
							<b-pagination
								v-if="totalRows > perPage"
								v-model="currentPage"
								:total-rows="totalRows"
								:per-page="perPage"
								@change="loadEvidences"
								size="sm"
								pills
								class="mb-0"
							></b-pagination>
						</div>

						<div class="evidence-list" style="height: 600px; overflow-y: auto;">
							<div
								v-for="item in rows"
								:key="item.id"
								class="card mb-2 evidence-item shadow-sm border"
								:class="{
									'selected': selectedItem && selectedItem.id === item.id,
									'border-success': item.evidenceStatus === 'approved',
									'border-danger': item.evidenceStatus === 'rejected'
								}"
								@click="selectEvidence(item)"
							>
								<div class="card-body py-2 px-3">
									<div class="d-flex justify-content-between align-items-center mb-2">
										<div>
											<span class="font-weight-bold text-primary">#{{ item.service.serviceNumber }}</span>
											<small class="text-muted ml-2">{{ item.service.trip }}</small>
											<div v-if="item.tokenExpireAt" class="text-right">
												<small :class="item.reminingTime > 0 ? 'text-muted' : 'text-danger'">
													<md-icon icon="schedule" /> Expira: {{ item.tokenExpireAt }}
													<span v-if="item.reminingTime > 0">
														(expira en {{ item.reminingTime }} {{ formatTimeUnit(item.reminingTimeUnit, item.reminingTime) }})
													</span>
													<span v-else class="font-weight-bold">
														(expirado)
													</span>
												</small>
											</div>
											<div class="text-muted">
												<small>
													<md-icon icon="warning" />
													{{ item.reasonState }}
												</small>
											</div>
										</div>
										<div>
											<b-badge
												:variant="getStatusVariant(item.evidenceStatus)"
												class="px-2 mr-2"
											>
												{{ getStatusLabel(item.evidenceStatus) }}
											</b-badge>
											<b-badge v-if="item.service.evidenceFiles && item.service.evidenceFiles.length" variant="info">
												<md-icon icon="photo_library" /> {{ item.service.evidenceFiles.length }}
											</b-badge>
											<b-badge v-else variant="secondary">
												Sin evidencias
											</b-badge>
											<b-button
												class="ml-2"
												variant="primary"
												size="sm"
												@click.stop="copyEvidenceLink(item)"
												title="Copiar link"
											>
												<md-icon icon="content_copy" />
											</b-button>
										</div>
									</div>

									<div class="row info-grid">
										<div class="col-3 info-row text-truncate">
											<md-icon icon="business" />
											<strong>{{ item.service.supplier }}</strong>
										</div>
										<div class="col-3 info-row text-truncate">
											<md-icon icon="person" />
											<span>{{ item.service.clientName }}</span>
										</div>
										<div class="col-3 info-row">
											<md-icon icon="event" />
											<span>{{ item.service.pickupDatetime }}</span>
										</div>
										<div class="col-3 info-row text-truncate">
											<md-icon icon="place" />
											<span>{{ item.service.pickup }}</span>
										</div>
									</div>

										<!-- Acciones de revisión dentro del card seleccionado -->
									<div v-if="selectedItem && selectedItem.id === item.id" class="review-panel mt-3 pt-3 border-top" @click.stop>
										<div v-if="item.evidenceProvidedAt" class="info-row text-info mb-2">
											<md-icon icon="cloud_upload" />
											<span>Cargado: {{ item.evidenceProvidedAt }}</span>
										</div>

										<div v-if="item.evidenceReviewedBy && item.evidenceReviewedAt" class="d-flex justify-content-between align-items-center mb-2">
											<div class="info-row" :class="item.evidenceStatus === 'approved' ? 'text-success' : 'text-danger'">
												<md-icon icon="verified_user" />
												<span>Revisado por {{ item.evidenceReviewedBy }} el {{ item.evidenceReviewedAt }}</span>
											</div>
											<b-button
												v-if="$hasPermission('suppliersEvidences.revert') && item.evidenceStatus !== 'pending'"
												v-b-tooltip.hover
												title="Restablecer a pendiente"
												variant="secondary"
												size="sm"
												:disabled="reviewLoading === 'revert'"
												@click="revertEvidence"
											>
												<b-spinner v-if="reviewLoading === 'revert'" small></b-spinner>
												<md-icon v-else icon="history" />
											</b-button>
										</div>

										<div v-if="item.evidenceRevertedBy && item.evidenceRevertedAt" class="info-row text-warning mb-2">
											<md-icon icon="history" />
											<span>Revertido por {{ item.evidenceRevertedBy }} el {{ item.evidenceRevertedAt }}</span>
										</div>

										<div v-if="item.evidenceStatus === 'pending'">
											<label class="small font-weight-bold mb-1">
												<md-icon icon="report_problem" /> Razón de rechazo:
											</label>
											<b-form-select
												v-if="$hasPermission('suppliersEvidences.update')"
												v-model="rejectionReason"
												:options="rejectionReasons"
												size="sm"
												class="mb-3"
											></b-form-select>
											<div class="d-flex">
												<b-button
													v-if="$hasPermission('suppliersEvidences.update')"
													variant="outline-danger"
													size="sm"
													class="flex-fill mr-2"
													:disabled="reviewLoading || !rejectionReason"
													@click="rejectEvidence"
												>
													<b-spinner v-if="reviewLoading === 'reject'" small></b-spinner>
													<template v-else>
														<md-icon icon="thumb_down" /> Rechazar
													</template>
												</b-button>
												<b-button
													v-if="$hasPermission('suppliersEvidences.update')"
													size="sm mr-2"
													class="flex-fill"
													:disabled="reviewLoading"
													@click="approveEvidence('noShow')"
													:variant="
														$root.auxVars.transportServiceStatusClasses
															.noShow
													"
													>

													<b-spinner v-if="reviewLoading === 'approve'" small></b-spinner>
													<template v-else>
														<md-icon icon="perm_identityhelp_outline" /> No show
													</template>
													
												</b-button>
												<b-button
													v-if="canMarkAsDone(item) && $hasPermission('suppliersEvidences.update')"
													size="sm"
													class="flex-fill"
													:disabled="reviewLoading"
													@click="approveEvidence('done')"
													:variant="
														$root.auxVars.transportServiceStatusClasses
															.done
													"
													>

													<b-spinner v-if="reviewLoading === 'approve'" small></b-spinner>
													<template v-else>
														<md-icon icon="done" /> Realizado
													</template>

												</b-button>
											</div>
										</div>
										<div v-else class="status-result p-2 rounded text-center"
											:class="item.evidenceStatus === 'approved' ? 'bg-success-light' : 'bg-danger-light'">
											<md-icon
												:icon="item.evidenceStatus === 'approved' ? 'verified' : 'cancel'"
												:class="item.evidenceStatus === 'approved' ? 'text-success' : 'text-danger'"
												style="font-size: 24px;"
											/>
											<div class="font-weight-bold">
												{{ item.evidenceStatus === 'approved' ? 'Aprobado' : 'Rechazado' }}
											</div>
											<small v-if="item.evidenceRejectionReason" class="text-muted d-block">
												{{ item.evidenceRejectionReason }}
											</small>
										</div>
									</div>
								</div>
							</div>
						</div>

					</div>

					<!-- Panel de evidencias (solo imágenes) -->
					<div class="col-md-5 sticky-panel" v-if="rows.length > 0 && !loading">
						<div v-if="!selectedItem" class="empty-state text-center text-muted">
							<div class="empty-state-icon">
								<md-icon icon="photo_library" />
							</div>
							<h5>Selecciona un servicio</h5>
							<p>Las evidencias se mostrarán aquí</p>
						</div>

						<div v-else class="evidence-gallery">
							<div class="d-flex justify-content-between align-items-center mb-3">
								<h6 class="mb-0">
									<md-icon icon="collections" />
									Evidencias de #{{ selectedItem.service.reservationId }}
								</h6>
								<b-badge variant="secondary">
									{{ selectedItem.service.evidenceFiles ? selectedItem.service.evidenceFiles.length : 0 }} archivos
								</b-badge>
							</div>

							<div v-if="!selectedItem.service.evidenceFiles || selectedItem.service.evidenceFiles.length === 0"
								class="empty-evidence text-center text-muted py-5 border rounded bg-light">
								<md-icon icon="image_not_supported" style="font-size: 64px;" class="text-muted" />
								<h6 class="mt-3">Sin evidencias</h6>
								<p class="small">El proveedor no ha cargado evidencias</p>
							</div>

							<div v-else class="evidence-carousel row">
								<div
									v-for="(evidence, index) in selectedItem.service.evidenceFiles"
									:key="index"
									class="col-6 mb-3"
								>
									<div class="evidence-thumb" @click="openImageModal(evidence.url)">
										<img
											:src="evidence.url"
											:alt="evidence.name"
											class="evidence-image rounded-top"
										/>
										<div class="evidence-label rounded-bottom">
											<span>
												<md-icon :icon="evidence.name.startsWith('gps') ? 'gps_fixed' : 'camera_alt'" />
												{{ evidence.name }}
											</span>
											<span class="badge badge-light text-uppercase">
												{{ evidence.type.split('/')[1] }}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal para ver imagen en grande -->
		<b-modal
			ref="imageModal"
			size="xl"
			hide-footer
			centered
			body-class="p-0"
			content-class="bg-dark"
		>
			<template #modal-header="{ close }">
				<div class="w-100 d-flex justify-content-between align-items-center text-white">
					<span>Vista previa</span>
					<b-button variant="link" class="text-white p-0" @click="close()">
						<md-icon icon="close" />
					</b-button>
				</div>
			</template>
			<img :src="modalImageUrl" class="w-100" style="max-height: 80vh; object-fit: contain;" />
		</b-modal>

		<DialogChangeStatus
			v-model="change.dialog"
			:status="change.status"
			:reasonState="change.reasonState"
			:serviceId="change.serviceId"
			:modelId="change.modelId"
			:requiredOption="false"
			@confirmed="onChangeStatusConfirmed"
			@reset="resetChange"
		/>

	</MainLayout>
</template>

<script>

import MainLayout from "../layout/MainLayout.vue";
import Multiselect from "vue-multiselect";
import DialogChangeStatus from "../reservation/formReservation/helpers/_dialogChangeState.vue";

// Razones de estado que permiten marcar el servicio como "Realizado"
const REASON_STATES_ALLOW_DONE = {
	FALTA_EVIDENCIAS_PROVEEDOR: 79,
	CONDUCTOR_NO_SE_PRESENTO: 78,
	DOWNGRADE_SERVICE: 94
};

export default {
	components: { MainLayout, Multiselect, DialogChangeStatus },
	data() {
		return {
			loading: false,
			rows: [],
			totalRows: 0,
			currentPage: 1,
			perPage: 10,
			perPageOptions: [
				{ value: 10, text: '10' },
				{ value: 25, text: '25' },
				{ value: 50, text: '50' },
				{ value: 100, text: '100' }
			],
			selectedItem: null,
			rejectionReason: null,
			reviewLoading: null,
			modalImageUrl: "",
			rejectionReasons: [],
			statusOptions: [
				{ value: "pending", text: "Pendientes" },
				{ value: "approved", text: "Aprobadas" },
				{ value: "rejected", text: "Rechazadas" },
				{ value: "", text: "Todas" }
			],
			filters: {
				id: "",
				suppliers: [],
				status: "pending",
				startDate: "",
				endDate: "",
				expiredLinks: false
			},
			change: {
				serviceId: "",
				status: "canceled",
				dialog: false,
				reasonState: 'notSelected'
			},
			pendingApproval: null,
		};
	},
	mounted() {
		this.setDefaultDates();
		this.loadRejectionReasons();
		this.loadEvidences();
		this.$root.auxVars.loadResourcesOpts({
			vm: this,
			resourceNames: [
				'suppliersOpts',
			]
		})
	},
	methods: {
		setDefaultDates() {
			const today = new Date();
			const sevenDaysAgo = new Date();
			sevenDaysAgo.setDate(today.getDate() - 7);

			this.filters.endDate = today.toISOString().split('T')[0];
			this.filters.startDate = sevenDaysAgo.toISOString().split('T')[0];
		},
		onPerPageChange() {
			this.currentPage = 1;
			this.loadEvidences();
		},
		async loadEvidences(page) {
			if (typeof page === 'number') {
				this.currentPage = page;
			}
			this.loading = true;
			this.selectedItem = null;
			try {
				const params = {};
				
				params.status 		= this.filters.status;
				params.suppliers 	= this.filters.suppliers.map(s => s.value).join(',');
				params.startDate 	= this.filters.startDate;
				params.endDate 		= this.filters.endDate;
				params.expiredLinks = this.filters.expiredLinks;
				params.id 		    = this.filters.id

				const response = await this.$root.api.get("/supplier-evidences", { 
					params: {filters:params, limit: this.perPage, offset: (this.perPage*(this.currentPage-1))} 
				});
				this.rows 	   = response.data.rows;
				this.totalRows = response.data.totalRows;
			} catch (error) {
				console.error("Error loading evidences:", error);
				this.$root.showAlert("Error al cargar las evidencias", { variant: "danger" });
			} finally {
				this.loading = false;
			}
		},
		async loadRejectionReasons() {
			const response = await this.$root.api.get("/supplier-evidences/rejectionReasons", {
				params: {lang: "es"}
			});

			this.rejectionReasons = [
				{ value: null, text: "Seleccione una razón...", disabled: true },
				...response.data
			];
		},
		getStatusVariant(status) {
			const variants = {
				pending: "warning",
				approved: "success",
				rejected: "danger"
			};
			return variants[status] || "secondary";
		},
		getStatusLabel(status) {
			const labels = {
				pending: "Pendiente",
				approved: "Aprobado",
				rejected: "Rechazado"
			};
			return labels[status] || status;
		},
		selectEvidence(item) {
			if (this.selectedItem && this.selectedItem.id === item.id) {
				this.selectedItem = null;
			} else {
				this.selectedItem = item;
			}
			this.rejectionReason = null;
		},
		openImageModal(url) {
			this.modalImageUrl = url;
			this.$refs.imageModal.show();
		},
		async approveEvidence(newStatus) {
			// const statusLabel = newStatus === 'noShow' ? 'No Show' : 'Realizado';
			/*
			const confirmed = await this.$bvModal.msgBoxConfirm(
				`¿Estás seguro de actualizar servicio a "${statusLabel}"?`,
				{
					title: 'Confirmar acción',
					okVariant: 'primary',
					okTitle: 'Continuar',
					cancelTitle: 'Cancelar',
					centered: true
				}
			);
			if (!confirmed) return;
			*/

			// Guardar información para aprobar después de confirmar el cambio de estado
			this.pendingApproval = {
				evidenceId: this.selectedItem.id,
				status: newStatus
			};

			// Abrir diálogo de cambio de estado
			this.changeStatus(newStatus);
		},
		async onChangeStatusConfirmed() {
			if (!this.pendingApproval) return;

			this.reviewLoading = "approve";
			try {
				await this.$root.api.post(`/supplier-evidences/${this.pendingApproval.evidenceId}/approved`, {
					status: this.pendingApproval.status, reason: ""
				});
				this.$root.showAlert("Evidencia aprobada correctamente", { variant: "success" });
				this.loadEvidences();
			} catch (error) {
				console.error("Error approving evidence:", error);
				this.$root.showAlert("Error al aprobar la evidencia", { variant: "danger" });
			} finally {
				this.reviewLoading = null;
				this.pendingApproval = null;
			}
		},
		async rejectEvidence() {
			if (!this.rejectionReason) {
				this.$root.showAlert("Seleccione una razón de rechazo", { variant: "warning" });
				return;
			}

			const confirmed = await this.$bvModal.msgBoxConfirm(
				'¿Estás seguro que desea rechazar esta evidencia? El servicio conservará el estado No atendido',
				{
					title: 'Confirmar rechazo',
					okVariant: 'danger',
					okTitle: 'Continuar',
					cancelTitle: 'Cancelar',
					centered: true
				}
			);
			if (!confirmed) return;

			this.reviewLoading = "reject";
			try {
				await this.$root.api.post(`/supplier-evidences/${this.selectedItem.id}/rejected`, {
					status: "", reason: this.rejectionReason
				});
				this.$root.showAlert("Evidencia rechazada", { variant: "warning" });
				this.loadEvidences();
			} catch (error) {
				console.error("Error rejecting evidence:", error);
				this.$root.showAlert("Error al rechazar la evidencia", { variant: "danger" });
			} finally {
				this.reviewLoading = null;
			}
		},
		async revertEvidence() {
			const confirmed = await this.$bvModal.msgBoxConfirm(
				'¿Estás seguro que desea revertir esta evidencia a estado pendiente?',
				{
					title: 'Confirmar reversión',
					okVariant: 'warning',
					okTitle: 'Continuar',
					cancelTitle: 'Cancelar',
					centered: true
				}
			);
			if (!confirmed) return;

			this.reviewLoading = "revert";
			try {
				await this.$root.api.post(`/supplier-evidences/${this.selectedItem.id}/revert`);
				this.$root.showAlert("Evidencia revertida a pendiente", { variant: "success" });
				this.loadEvidences();
			} catch (error) {
				console.error("Error reverting evidence:", error);
				this.$root.showAlert("Error al revertir la evidencia", { variant: "danger" });
			} finally {
				this.reviewLoading = null;
			}
		},
		copyEvidenceLink(item) {
			const baseUrl = process.env.VUE_APP_SUPPLIERS.replace(/\/$/, '');
			const link = `${baseUrl}/supplier-evidences?token=${item.token}`;
			navigator.clipboard.writeText(link).then(() => {
				this.$root.showAlert("Link copiado al portapapeles", { variant: "success" });
			}).catch(() => {
				this.$root.showAlert("Error al copiar el link", { variant: "danger" });
			});
		},
		changeStatus(status) {
			this.change.serviceId   = this.selectedItem.service.id;
			this.change.modelId     = this.selectedItem.service.reservationId; 
			this.change.status 		= status;
			this.change.dialog 		= true;
			this.change.reasonState = "";
		},
		resetChange() {
			this.change.serviceId = "";
			this.change.status = "canceled";
			this.change.dialog = false;
			this.pendingApproval = null;
			this.loadEvidences()
		},
		clearFilters() {
			this.filters = {
				suppliers: [],
				status: "pending",
				startDate: "",
				endDate: "",
				expiredLinks: false
			};
			this.setDefaultDates();
			this.loadEvidences();
		},
		canMarkAsDone(item) {
			const allowedReasons = Object.values(REASON_STATES_ALLOW_DONE);
			return allowedReasons.includes(item.service.reasonStateId);
		},
		formatTimeUnit(unit, value) {
			const units = {
				hrs: value === 1 ? 'hora' : 'hora(s)',
				min: value === 1 ? 'minuto' : 'minuto(s)'
			};
			return units[unit] || unit;
		}
	},
	computed: {
		supplierOptions() {
			let aux= [...this.$root.auxVars.suppliersOpts.map((sc) => ({
				value: sc.id,
				text: sc.name,
				}))];
			return aux;
		}
	}
};
</script>

<style scoped>
.evidence-item {
	cursor: pointer;
	transition: all 0.15s ease;
	border-left: 4px solid #dee2e6;
	border-radius: 6px;
	background: #fff;
}
.evidence-item:hover {
	transform: translateX(4px);
	box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
	border-left-color: #007bff;
}
.evidence-item.selected {
	border-left-color: #007bff !important;
	background: linear-gradient(to right, #e8f4ff, #fff);
	box-shadow: 0 2px 12px rgba(0,123,255,0.15) !important;
}
.evidence-item.border-success {
	border-left-color: #28a745 !important;
}
.evidence-item.border-danger {
	border-left-color: #dc3545 !important;
}
.info-grid {
	margin: 0;
	font-size: 0.78rem;
	background: #f8f9fa;
	border-radius: 4px;
	padding: 6px 8px;
	margin-top: 8px;
}
.info-row {
	font-size: 0.78rem;
	color: #495057;
	margin-bottom: 0;
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 0;
}
.info-row .material-icons {
	font-size: 13px;
	color: #6c757d;
}
.review-panel {
	margin: 12px -12px -8px -12px;
	padding: 12px;
	border-radius: 0 0 4px 4px;
	background: linear-gradient(to bottom, #f1f3f5, #e9ecef);
	border-top: 1px dashed #ced4da;
}
.evidence-list {
	padding-right: 8px;
}
.evidence-list::-webkit-scrollbar {
	width: 6px;
}
.evidence-list::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 3px;
}
.evidence-list::-webkit-scrollbar-thumb {
	background: #c1c1c1;
	border-radius: 3px;
}
.evidence-list::-webkit-scrollbar-thumb:hover {
	background: #a1a1a1;
}
.card-body .font-weight-bold {
	font-size: 0.95rem;
}
.sticky-panel {
	position: sticky;
	top: 20px;
	align-self: flex-start;
	max-height: calc(100vh - 200px);
	overflow-y: auto;
}
::v-deep .pagination .page-link {
	min-width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 500;
	border-radius: 6px !important;
	margin: 0 2px;
}
::v-deep .pagination .page-item.active .page-link {
	background-color: #007bff;
	border-color: #007bff;
}
.empty-state {
	padding: 80px 20px;
	background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
	border-radius: 8px;
	height: 600px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
}
.empty-state-icon {
	width: 100px;
	height: 100px;
	background: #fff;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 20px;
	box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}
.empty-state-icon .material-icons {
	font-size: 48px;
	color: #6c757d;
}
.evidence-thumb {
	cursor: zoom-in;
	transition: transform 0.2s ease;
	box-shadow: 0 2px 8px rgba(0,0,0,0.1);
	border-radius: 8px;
	overflow: hidden;
}
.evidence-thumb:hover {
	transform: scale(1.02);
	box-shadow: 0 4px 16px rgba(0,0,0,0.2);
}
.evidence-image {
	width: 100%;
	height: 180px;
	object-fit: cover;
	background-color: #f0f0f0;
}
.evidence-label {
	background: #f8f9fa;
	padding: 8px 10px;
	font-size: 0.8rem;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-top: 1px solid #eee;
}
.evidence-label .material-icons {
	font-size: 14px;
	margin-right: 4px;
}
.evidence-carousel {
	max-height: 600px;
	overflow-y: auto;
}
.evidence-carousel::-webkit-scrollbar {
	width: 6px;
}
.evidence-carousel::-webkit-scrollbar-thumb {
	background-color: #ccc;
	border-radius: 3px;
}
.bg-success-light {
	background-color: #d4edda;
	border: 1px solid #c3e6cb;
}
.bg-danger-light {
	background-color: #f8d7da;
	border: 1px solid #f5c6cb;
}
.status-result .material-icons {
	display: block;
	margin: 0 auto 8px;
}
</style>
